// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createVerticalSmallHeadingAndTextStyles } from "@eisinc/App/SharedStyles/smallHeadingAndTextStyles";
import { ButtonWrapper } from "@eisinc/MobiusOverrides/Button";
import { productListLineStyles } from "@eisinc/Overrides/Widgets/MyListsDetails/MyListsDetailsProductListLine";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, sharedProductDescriptionStyles, xScreen } from "@eisinc/XDL/Utilities";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

productListLineStyles.discontinuedProductDescription = { ...sharedProductDescriptionStyles().productDetailDescription };

productListLineStyles.descriptionGridItem = {
    css: css`
        margin-bottom: 11px;
        height: 40px;
        @media print {
            height: auto;
        }
        * {
            font-size: 16px;
            line-height: 20px;
        }
    `,
};

productListLineStyles.partNumbersGridItem = {
    css: css`
        display: flex;
        align-items: center;
        margin-bottom: 11px;
        height: auto;
        @media print {
            margin-top: 14px;
        }
    `,
};

productListLineStyles.infoContainer = {
    css: css`
        width: 100%;
        &.info-list-view {
            height: 100%;
            > div {
                &:last-child {
                    height: calc(100% - 51px);
                }
            }
        }
    `,
};

productListLineStyles.listInfoWrapper = {
    css: css`
        display: flex;
        > div {
            flex: 1;

            &:first-child {
                padding-right: 15px;
            }
        }
        ${xScreen("lte", "sm")} {
            flex-direction: column;

            > div {
                width: 100%;
                padding-tight: 0;
                padding-bottom: 5px;
            }
        }
    `,
};

productListLineStyles.numbersBrandWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    `,
};

productListLineStyles.brandStyles = {
    logoImage: {
        width: "auto",
        height: "40px",
        css: css`
            max-width: 100%;

            @media print {
                max-width: 0%;
            }
            img {
                max-width: 100%;
                max-height: 100%;
                width: auto;
                height: auto;
            }
        `,
    },
    nameLink: {
        color: "primary.main",
    },
    nameText: {
        color: "text.main",
    },
};

productListLineStyles.badgesAvailabilityWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    `,
};

productListLineStyles.listAvailabilityStyles = {
    containerWithAdditionalMessage: {
        css: css`
            display: flex;

            @media print {
                flex-direction: column;
                .separator {
                    display: none;
                }
            }

            @media only screen and (max-width: 1439px) {
                flex-direction: column;

                .separator {
                    display: none;
                }
            }
        `,
    },
};

productListLineStyles.badgesContainerStyle = {
    css: css`
        display: flex;
        &.info-list-view-badges {
            button {
                padding: 0;
                width: 21px;
                height: 21px;
            }

            img {
                max-width: 21px;
                max-height: 21px;
                overflow: hidden;
            }
        }
    `,
};

productListLineStyles.container = {
    css: css`
        display: flex;
        align-items: stretch;
        width: 100%;
        @media only screen and (max-width: 1270px) {
            flex-wrap: wrap;
        }
    `,
};

productListLineStyles.imageWrapper = {
    css: css`
        width: 175px;
        height: 175px;
        display: flex;
        justify-content: center;
        align-items: flex-start;
        max-width: 100%;

        img {
            max-width: 155px;
            max-height: 155px;
            overflow: hidden;
        }

        @media only screen and (max-width: 550px) {
            width: 100px;
            height: 100px;
        }
    `,
};

productListLineStyles.imageWrapperDetails = {
    css: css`
        display: flex;
        justify-content: space-around;
        align-items: center;
        width: 100%;
    `,
};

productListLineStyles.infoWrapper = {
    css: css`
        width: calc(100% - 505px);
        padding-left: 20px;
        padding-right: 20px;

        @media only screen and (max-width: 1270px) {
            width: calc(100% - 175px);
        }

        @media only screen and (max-width: 550px) {
            width: calc(100% - 100px);
        }
    `,
};

productListLineStyles.actionsWrapper = {
    css: css`
        width: 330px;
        padding-left: 12px;
        border-left: 1px solid ${color("common.ui3")};
        padding-top: 10px;

        @media print {
            border-left: none;
        }

        @media only screen and (max-width: 1270px) {
            width: 100%;
            padding-left: 195px;
            border-left: 0;
        }

        @media only screen and (max-width: 550px) {
            padding-left: 0;
        }
    `,
};

productListLineStyles.actionsWrapperInner = {
    css: css`
        display: flex;
        width: 100%;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        gap: 8px;
    `,
};

productListLineStyles.unitOfMeasureSelect = {
    cssOverrides: {
        formField: css`
            select {
                padding-right: 13px !important;
            }

            select + span {
                padding-right: 0 !important;
                width: 22px !important;
                right: 2px !important;
                top: 0 !important;
            }
        `,
        inputSelect: css`
            @media print {
                border: none;
            }
        `,
        formInputWrapper: css`
            @media print {
                width: 120px;
                min-width: 120px;
                border: none;
            }
        `,
    },
};

productListLineStyles.priceWrapper = {
    css: css`
        min-height: 22px;
        display: flex;
        > section {
            width: auto;
        }
        * {
            line-height: 22px;
            font-size: 20px;
            font-weight: normal;
            color: ${color("text.main")};
        }
    `,
};

productListLineStyles.addToCartWrapper = {
    css: css`
        display: flex;
        width: 100%;
        gap: 8px;
        & > div:last-child {
            flex: 1;
            display: flex;
            ${ButtonWrapper} {
                flex: 1;
            }
        }
    `,
};

productListLineStyles.uomWrapper = {
    css: css`
        max-width: 92px;
    `,
};

productListLineStyles.addToCartButton = { sizeVariant: "small" };

productListLineStyles.unitOfMeasureSelect = { sizeVariant: "small" };

productListLineStyles.chooseOptionsClickableStyles = {
    css: css`
        display: block;
        width: 100%;
        font-size: 18px;
        letter-spacing: 0.5px;

        &.list-view-options-button {
            margin-top: 36px;
        }

        &:focus {
            outline-width: 0;
        }
        > button {
            width: 100%;

            ${TypographyStyle} {
                font-size: 14px;
            }
        }
    `,
};

productListLineStyles.restrictedMessageText = { color: "danger" };

productListLineStyles.notesStyles = {
    ...createVerticalSmallHeadingAndTextStyles({
        wrapper: {
            css: css`
                margin-top: 12px;
            `,
        },
        heading: {
            ...fontConfig.singleLinexSmall,
        },
        text: {
            ...fontConfig.singleLinexSmall,
        },
    }),
};

productListLineStyles.selectedCheckbox = {
    css: css`
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.productPartNumbersStyles = {

};

productListLineStyles.customImagesBadgesStyle = {
    triggererWrapper: {
        css: css`
        margin-left: 5px;`,
    },
};